import { GridItem, InputRightElement } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import { YesNo } from "../../lib/models/enums";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";
import { SelectItem } from "../../lib/types/SelectItem";
import CheckboxField from "../form-fields/CheckboxField";
import SelectField from "../form-fields/SelectField";
import TextInputField from "../form-fields/TextInputField";
import ExceptionOptions from "./ExceptionOptions";
import RateOverride from "./RateOverride";

export type ExceptionOptionsProps = {
  handler: (status: string | undefined) => void;
  findScenarioPercentages: (baseRate: number) => number;
  stfCalc: FixAndFlipCalculation | undefined;
  rentalCalc: RentalCalculation | undefined;
  rateReductionOverride: any;
  showCommission: boolean;
  maxRate: any;
  isCalculating: boolean;
  disableInputs: boolean;
  disableCommission: boolean;
};

function ManualException({
  handler,
  findScenarioPercentages,
  stfCalc,
  rentalCalc,
  rateReductionOverride,
  showCommission,
  maxRate,
  isCalculating,
  disableInputs,
  disableCommission,
}: ExceptionOptionsProps) {
  const { values } = useFormikContext<any>();
  return (
    <>
      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          name="finalOrigFeeOverride"
          label="Final Origination Fee Override"
          type="number"
          children={<InputRightElement children="%" />}
          helperText={`Current Fee: ${
            stfCalc
              ? stfCalc?.RateFeeLLPA.finalOrigFee
              : rentalCalc?.originationFeePercent
          }%`}
          placeholder="No override"
          disabled={disableInputs}
        />
      </GridItem>

      <>
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <TextInputField
            name="processingFeeOverride"
            label="Underwriting and Processing Fee Override"
            type="number"
            placeholder="No override"
            disabled={disableInputs}
            helperText={`Current Fee: $${
              stfCalc?.LoanCosts.processingFee ??
              rentalCalc?.scenarios[0].processingFee
            }`}
          />
        </GridItem>
      </>

      {stfCalc && (
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              name="blendedLTCOverride"
              label="Blended LTC Override"
              type="number"
              children={<InputRightElement children="%" />}
              helperText={`Current Blended LTC: ${stfCalc?.BlendedLTCCalc.maxBlendedLTCRatio}%`}
              placeholder="No override"
              disabled={disableInputs}
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              name="purchaseLTCOverride"
              label="Purchase LTC Override"
              type="number"
              children={<InputRightElement children="%" />}
              helperText={`Current Purchase LTC: ${stfCalc?.LTCCalcOnPurchaseRehab.maxLTCOnPurchase}%`}
              placeholder="No override"
              disabled={disableInputs}
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              name="arvOverride"
              label="ARV Override"
              type="number"
              children={<InputRightElement children="%" />}
              helperText={`Current ARV: ${stfCalc?.LTVARVCalc.maxLTVRatio}%`}
              placeholder="No override"
              disabled={disableInputs}
            />
          </GridItem>
        </>
      )}
      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="ACH Exempt?"
          name="achExempt"
          placeholder=""
          data={selectACH}
          disabled={disableInputs || values.achRequired === YesNo.No}
        />
      </GridItem>

      <GridItem alignSelf="center" colSpan={{ base: 12, lg: 6 }}>
        <CheckboxField
          label="ACH Required?"
          name="achRequired"
          disabled={disableInputs}
        />
      </GridItem>

      {/* {rentalCalc && (
        <GridItem alignSelf="center" colSpan={{ base: 12, lg: 6 }}>
          <CheckboxField
            label="Split Processing Fee?"
            name="splitProcessingFee"
            disabled={disableInputs}
          />
        </GridItem>
      )} */}

      {stfCalc ? (
        <RateOverride
          rateReduction={rateReductionOverride}
          currRate={stfCalc?.RateFeeLLPA.finalRate}
          disableInputs={disableInputs}
        />
      ) : (
        <RateOverride
          scenarios={rentalCalc?.scenarios}
          isCalculating={isCalculating}
          maxRate={maxRate}
          rateReduction={rateReductionOverride}
          handleScenarioPercentages={findScenarioPercentages}
          disableInputs={disableInputs}
        />
      )}

      {showCommission && (
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              name="commissionReduction"
              label="Commission Reduction"
              type="number"
              children={<InputRightElement children="%" />}
              placeholder="No override"
              disabled={disableCommission}
            />
          </GridItem>
          <GridItem colSpan={{ base: 12, lg: 6 }}></GridItem>
        </>
      )}

      <ExceptionOptions handler={handler} />
    </>
  );
}

export default ManualException;

export const selectRate: SelectItem[] = [
  { label: "0.000%", value: "0" },
  { label: "-0.120%", value: "0.12" },
  { label: "-0.250%", value: "0.25" },
];

export const selectACH: SelectItem[] = [
  { label: "Exempt", value: "Exempt" },
  { label: "Non-exempt", value: "Non-exempt" },
];
